import React, { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Signature cursor:
 *  - Default: tiny white dot in mix-blend-difference (inverts anything underneath)
 *  - Over portfolio cards (`data-cursor="view"`): grows into a 88px "View" circle
 *  - Hidden on touch devices
 */
export const CustomCursor = () => {
  const [enabled, setEnabled] = useState(false);
  const [variant, setVariant] = useState("default");
  const [visible, setVisible] = useState(false);
  const downRef = useRef(false);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 450, damping: 40, mass: 0.35 });
  const sy = useSpring(y, { stiffness: 450, damping: 40, mass: 0.35 });

  useEffect(() => {
    const fine = window.matchMedia?.("(pointer: fine)").matches;
    setEnabled(!!fine);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      if (!visible) setVisible(true);
    };
    const onEnter = () => setVisible(true);
    const onLeave = () => setVisible(false);
    const onDown = () => (downRef.current = true);
    const onUp = () => (downRef.current = false);

    const onOver = (e) => {
      const t = e.target instanceof Element ? e.target.closest('[data-cursor="view"]') : null;
      setVariant(t ? "view" : "default");
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    document.documentElement.addEventListener("mouseenter", onEnter);
    document.documentElement.addEventListener("mouseleave", onLeave);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      document.documentElement.removeEventListener("mouseenter", onEnter);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
    };
  }, [enabled, visible, x, y]);

  if (!enabled) return null;

  const isView = variant === "view";
  const size = isView ? 88 : 14;

  return (
    <>
      <style>{`
        @media (pointer: fine) {
          html, body, a, button, [role="button"], input, textarea, select, label, img { cursor: none !important; }
        }
      `}</style>

      <motion.div
        aria-hidden="true"
        style={{ translateX: sx, translateY: sy }}
        className="fixed top-0 left-0 z-[200] pointer-events-none mix-blend-difference"
      >
        <motion.div
          animate={{
            width: size,
            height: size,
            opacity: visible ? 1 : 0,
            scale: downRef.current ? 0.82 : 1,
          }}
          transition={{ type: "spring", stiffness: 320, damping: 28, mass: 0.4 }}
          className="rounded-full bg-white flex items-center justify-center -translate-x-1/2 -translate-y-1/2 will-change-transform"
          style={{ color: "#000" }}
        >
          <motion.span
            animate={{ opacity: isView ? 1 : 0 }}
            transition={{ duration: 0.15 }}
            className="text-[10px] tracking-[0.25em] uppercase font-bold select-none"
          >
            View
          </motion.span>
        </motion.div>
      </motion.div>
    </>
  );
};
